/**
 * Native-app (Capacitor shell) integration for the web app.
 *
 * The mobile app (mobile/) loads this same web app inside a Capacitor
 * WebView in remote mode; Capacitor injects a `window.Capacitor` bridge
 * with the installed native plugins. On the plain website (and during
 * SSR) none of this exists, so every export is a safe no-op there.
 *
 * Push registration flow (design record: docs/design/mobile-app.md §Push):
 *  1. After the current user is fetched (logged in, inside the app),
 *     request notification permission and register with FCM/APNs.
 *  2. The OS hands back a device token; we store it on the user's
 *     Sharetribe `privateData.pushTokens` (Sharetribe-first — no custom
 *     database). The server's push sender reads tokens from there.
 *  3. Tapping a notification deep-links to the URL the server put in the
 *     notification's data payload.
 */

// Max device tokens kept per user (old phones age out, newest first).
const MAX_TOKENS = 5;

const getCapacitor = () =>
  typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform?.()
    ? window.Capacitor
    : null;

export const isNativeApp = () => !!getCapacitor();

// Module-level guard: register once per page lifetime, not on every
// currentUser refetch.
let registrationStarted = false;

/**
 * Ask for permission, register for push, and persist the device token on
 * the current user's privateData. Fire-and-forget: errors are logged,
 * never thrown into the calling duck.
 *
 * @param {Object} sdk Sharetribe Marketplace SDK (authenticated)
 * @param {Object} currentUser the fetched current user (for existing tokens)
 */
export const registerPushIfNativeApp = (sdk, currentUser) => {
  const cap = getCapacitor();
  if (!cap || registrationStarted) return;
  const Push = cap.Plugins?.PushNotifications;
  if (!Push) {
    // Shell version without the push plugin (pre-Phase-3 build): fail
    // loud in the console, not silently.
    console.warn('[nativeApp] running in shell but PushNotifications plugin is missing');
    return;
  }
  registrationStarted = true;

  const platform = cap.getPlatform(); // 'ios' | 'android'

  Push.addListener('registration', async ({ value: token }) => {
    try {
      const existing =
        currentUser?.attributes?.profile?.privateData?.pushTokens || [];
      const withoutThis = existing.filter(t => t && t.token !== token);
      const pushTokens = [
        { token, platform, updatedAt: new Date().toISOString() },
        ...withoutThis,
      ].slice(0, MAX_TOKENS);
      await sdk.currentUser.updateProfile({ privateData: { pushTokens } });
    } catch (e) {
      console.error('[nativeApp] failed to save push token', e);
    }
  });

  Push.addListener('registrationError', e => {
    console.error('[nativeApp] push registration error', e);
  });

  // Notification tapped: deep-link to the page the server put in `data.url`.
  // Same-origin paths only — never navigate to a URL from outside our server.
  Push.addListener('pushNotificationActionPerformed', action => {
    const url = action?.notification?.data?.url;
    if (typeof url === 'string' && url.startsWith('/')) {
      window.location.assign(url);
    }
  });

  Push.requestPermissions()
    .then(({ receive }) => {
      if (receive === 'granted') {
        return Push.register();
      }
      console.warn('[nativeApp] push permission not granted:', receive);
      return null;
    })
    .catch(e => console.error('[nativeApp] push permission request failed', e));
};
